import * as React from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
} from "@mui/material";
import StyledTypography from "../../helper/styled_typography";
import { styled } from "@mui/material/styles";
import { saveSearchApi } from "../../api/searchApi";
import { createWorkspaceApi } from "../../api/workspaceApi";
import { errorDetail } from "../../helper/errorDetail";

const dialogButtonSx = {
  textTransform: "none",
  fontWeight: 700,
  fontSize: 13,
  lineHeight: 1.2,
  minHeight: 32,
  minWidth: 72,
  px: 1.5,
  borderRadius: "10px",
};

const StyledSaveButton = styled(Button)(({ theme }) => ({
  ...dialogButtonSx,
  backgroundColor: "#4A2CF5",
  color: theme.palette.common.white,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#3B22D4",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(74,44,245,0.25)",
    color:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.8)",
  },
}));

const StyledCancelButton = styled(Button)(({ theme }) => ({
  ...dialogButtonSx,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.18)" : "rgba(88,89,91,0.22)",
  backgroundColor: "transparent",
  "&:hover": {
    borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.35)" : "rgba(88,89,91,0.4)",
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(23,15,74,0.04)",
  },
}));

const GENERIC_SAVE_ERROR = "An unexpected error occurred. Please try again.";

/** FastAPI may return detail as a string, object, or validation array. */
export function formatSaveSearchError(detail, fallback = GENERIC_SAVE_ERROR) {
  return errorDetail(detail, fallback);
}

/**
 * @param {"legacy" | "workspace"} persistMode
 *   - legacy: POST /save-search/ (V1–V3)
 *   - workspace: POST /workspaces with query (V4 Saved Search)
 */
export default function SaveSearchDialog({
  open,
  onClose,
  searchSpec,
  onSaveSuccess,
  persistMode = "legacy",
}) {
  const [searchName, setSearchName] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const [nameError, setNameError] = React.useState("");

  const resetAndClose = () => {
    setSearchName("");
    setNameError("");
    setIsSaving(false);
    onClose();
  };

  const handleClose = () => {
    if (isSaving) return;
    resetAndClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedName = searchName.trim();
    if (trimmedName === "") {
      return;
    }

    const query = searchSpec?.query;
    if (!query || typeof query !== "object") {
      setNameError("Nothing to save. Run a search with filters first.");
      return;
    }

    setNameError("");
    setIsSaving(true);

    try {
      if (persistMode === "workspace") {
        const res = await createWorkspaceApi({
          name: trimmedName,
          query,
        });

        if (res && (res.status === 200 || res.status === 201) && res.data) {
          onSaveSuccess?.(res.data);
          resetAndClose();
          return;
        }

        setIsSaving(false);
        if (res?.status === 403 && res.data?.detail?.error_code === "WORKSPACE_LIMIT_EXCEEDED") {
          setNameError(
            errorDetail(res.data?.detail,
              "You have reached the saved search limit. Delete an existing one to create another."
            )
          );
        } else if (res?.status === 409) {
          setNameError(
            errorDetail(res.data?.detail,
              "A saved search with this name already exists."
            )
          );
        } else if (
          res?.status === 422 &&
          res.data?.detail &&
          String(res.data.detail).includes("returned no companies")
        ) {
          setNameError("This query returned no companies. Please broaden your search and try again.");
        } else if (res?.status === 422) {
          setNameError(
            errorDetail(res.data?.detail, "Invalid search configuration provided.")
          );
        } else {
          setNameError(errorDetail(res?.data?.detail, GENERIC_SAVE_ERROR));
        }
        return;
      }

      const response = await saveSearchApi({
        name: trimmedName,
        spec: query,
      });
      onSaveSuccess?.(response?.data);
      resetAndClose();
    } catch (error) {
      console.error("Error saving search:", error);
      setIsSaving(false);

      if (error.response && error.response.status === 409) {
        setNameError("A saved search with this name already exists.");
      } else {
        setNameError("An unexpected error occurred. Please try again.");
      }
    }
  };

  const isButtonDisabled = searchName.trim() === "" || isSaving;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: "14px",
          width: "100%",
          maxWidth: 480,
          m: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.5,
          pt: 2,
          pb: 0,
        }}
      >
        <StyledTypography variant="h6" sx={{ fontSize: "18px" }}>
          Save Search
        </StyledTypography>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 2.5,
          pt: "10px !important",
          pb: 1.5,
        }}
      >
        <Typography
          component="p"
          sx={{
            m: 0,
            mb: 1.5,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.45,
            color: "#58595B",
          }}
        >
          Please enter a name for this search configuration so you can easily retrieve it later.
        </Typography>

        <form onSubmit={handleSubmit} id="save-search-form">
          <TextField
            autoFocus
            required
            size="small"
            id="searchName"
            name="searchName"
            type="text"
            fullWidth
            variant="outlined"
            placeholder="e.g., Texas Software Leads"
            value={searchName}
            error={!!nameError}
            helperText={nameError}
            onChange={(e) => {
              setSearchName(e.target.value);
              if (nameError) setNameError("");
            }}
            FormHelperTextProps={{
              sx: { mx: 0, mt: 0.75 },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                minHeight: 36,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "#161616" : "#F7F7F8",
                "& fieldset": {
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(88,89,91,0.14)",
                },
                "&:hover fieldset": {
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.2)"
                      : "rgba(74,44,245,0.35)",
                },
                "&.Mui-focused": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
                  "& fieldset": {
                    borderColor: nameError ? undefined : "#4A2CF5",
                  },
                },
              },
              "& .MuiInputBase-input": {
                fontSize: 13,
                fontWeight: 500,
                py: "8px",
                px: "12px",
              },
            }}
          />
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          pt: 0.5,
          pb: 2,
          gap: "8px",
          "& > :not(style) ~ :not(style)": {
            ml: 0,
          },
        }}
      >
        <StyledCancelButton variant="outlined" onClick={handleClose}>
          Cancel
        </StyledCancelButton>
        <StyledSaveButton
          type="submit"
          form="save-search-form"
          variant="contained"
          disableElevation
          disabled={isButtonDisabled}
        >
          {isSaving ? <CircularProgress size={16} color="inherit" /> : "Save"}
        </StyledSaveButton>
      </DialogActions>
    </Dialog>
  );
}
